'use client';
import { useScopeCalculations } from '@hyphengroup/cms-components/calculatedAttributes/store';
import { resolveFilterCategories } from '@hyphengroup/cms-components/cms-blocks/SortAndFilterPanel/deriveFilterOptions';
import { SORT_FILTER_RESULTS_ATTR } from '@hyphengroup/cms-components/cms-blocks/SortAndFilterPanel/FilteredLinkGate';
import { ProductEligibilityProvider } from '@hyphengroup/cms-components/cms-blocks/SortAndFilterPanel/ProductEligibilityProvider';
import { ProductFilterProvider } from '@hyphengroup/cms-components/cms-blocks/SortAndFilterPanel/ProductFilterProvider';
import type { FilterableProduct } from '@hyphengroup/cms-components/cms-blocks/SortAndFilterPanel/productFilterTypes';
import { ProductSortProvider } from '@hyphengroup/cms-components/cms-blocks/SortAndFilterPanel/ProductSortProvider';
import { ResultsSummary } from '@hyphengroup/cms-components/cms-blocks/SortAndFilterPanel/ResultsSummary';
import type { ResultNoun } from '@hyphengroup/cms-components/cms-blocks/SortAndFilterPanel/ResultsSummary';
import {
  ATTR_NAMES,
  AttrValue,
} from '@hyphengroup/cms-components/types/attrNames';
import type { TravelInsuranceQuotesData } from '@hyphengroup/cms-components/types/travelInsurance';
import { arePremiumsPending } from '@hyphengroup/cms-components/utils/travelInsurance/premiumsPending';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useClientContext } from '../../../../modules/context/client';
import { getBlockSkipKey } from '../../helpers/blockSkipKey';
import { CmsComponentProps, VerticalType } from '../../types';
import {
  BlockMapper,
  BlockMapperProps,
  getVisibleBlocks,
  PlainContainer,
} from '../BlockMapper';
import { ActiveFilterChipsWrapper } from './ActiveFilterChipsWrapper';
import { TI_QUOTE_STATUS_SLOT_ID } from './quoteStatusSlot';
import { SortAndFilterPanelWrapper } from './SortAndFilterPanelWrapper';
import {
  ComponentProductMetadata,
  EnumDisplayType,
} from '$/(cms)/_modules/cmsService/types';
import type { CmsSortFilterPanel } from '$/(cms)/_modules/cmsService/types/components';

/**
 * The block typenames, from the component union rather than as free strings: a
 * typo in one of the lists below reads as "not a sorted block", which silently
 * puts the panel beside content that never reorders.
 */
type BlockTypename = NonNullable<CmsComponentProps['__typename']>;

/**
 * The blocks the panel orders, and so the only ones that belong beside it. The
 * row has to end somewhere: a full-bleed background below the results reaches
 * the viewport edge only from a centred container.
 *
 * Not the same list as the sticky range ends (`LEGACY_RANGE_END_BLOCKS` in
 * `loanScope/filterSlot.ts`, the tabs slot in `PortalPlace.tsx`), which omit
 * `SelectedProducts` — that block is ordered here but ends no sticky range.
 */
const SORTED_PRODUCT_TYPENAMES: readonly BlockTypename[] = [
  'ComponentPagesComponentComparisonTable',
  'ComponentPagesComponentSelectedProducts',
  'ComponentPagesComponentCardListingPosition',
  'ComponentPagesComponentCcCardListing',
];

/**
 * The noun the result count uses. The block carries no vertical, so the app
 * names it, the same way it states the Clear All behaviour. Verticals not
 * listed get no count rather than another vertical's noun — a credit card page
 * must not be told it found a plan.
 */
const RESULT_NOUNS: Partial<Record<VerticalType, ResultNoun>> = {
  PERSONAL_LOANS: 'PERSONAL_LOAN',
  TRAVEL_INSURANCE: 'PLAN',
  CREDIT_CARD: 'CREDIT_CARD',
};

const resultNoun = (vertical: VerticalType | undefined) =>
  vertical && RESULT_NOUNS[vertical];

/**
 * The verticals routing a Selected Products block to `ComparisonTableWrapper`;
 * the others render fe-services tables, which never read the sort context
 * (`SelectedProducts.tsx:114`, `:132`).
 */
const SORT_AWARE_SELECTED_PRODUCT_VERTICALS: readonly VerticalType[] = [
  'CREDIT_CARD',
  'PERSONAL_LOANS',
];

/**
 * The same rule for a Comparison Table block, which routes through
 * `ComparisonTableMap` (`:64-72`, `:106`). An allow-list rather than "all but
 * INSURANCE", so a vertical added to that switch later stays correct here.
 */
const SORT_AWARE_COMPARISON_TABLE_VERTICALS: readonly VerticalType[] = [
  'CREDIT_CARD',
  'PERSONAL_LOANS',
  'INVESTMENTS',
  'BANKING',
  'PERSONAL_FINANCE',
  'TELCO',
  'CAR_INSURANCE',
  'TRAVEL_INSURANCE',
];

/**
 * The verticals the "Your details" group renders on (CMS-1905).
 *
 * Gated on the vertical rather than on the data, because the data does not
 * separate them: credit cards publish `MINIMUM_ANNUAL_INCOME` too
 * (`attrHelpers.ts`, the credit-card `eligibility` group) and `CREDIT_CARD` is
 * sort-aware, so a presence check would put a personal-loan eligibility box on
 * a credit-card page and play its cards down. The ticket scopes this to the
 * Personal Loan comparison page; adding a vertical is a deliberate decision.
 */
const ELIGIBILITY_VERTICALS: readonly VerticalType[] = ['PERSONAL_LOANS'];

/**
 * Typename alone is not enough for Selected Products: only `COMPARISON_TABLE`
 * reaches a sort-aware table. `vertical` is the page's, from this component's
 * props — `mapGqlSelectedProducts` never maps one onto the block.
 */
const isSortedProductBlock = (
  component: Pick<CmsComponentProps, '__typename'> | undefined,
  vertical: VerticalType | undefined,
) => {
  if (!component?.__typename) return false;
  if (!SORTED_PRODUCT_TYPENAMES.includes(component.__typename)) return false;

  if (component.__typename === 'ComponentPagesComponentComparisonTable') {
    return (
      !!vertical && SORT_AWARE_COMPARISON_TABLE_VERTICALS.includes(vertical)
    );
  }

  // Unconditional: all four `CardListingWrapper` variants render the library
  // block, which reads the context on every vertical; so does `CcCardListing`.
  if (component.__typename !== 'ComponentPagesComponentSelectedProducts')
    return true;

  const { display_type: displayType } = component as {
    display_type?: string | null;
  };

  return (
    displayType === EnumDisplayType.ComparisonTable &&
    !!vertical &&
    SORT_AWARE_SELECTED_PRODUCT_VERTICALS.includes(vertical)
  );
};

/**
 * The relation names the page's products actually carry.
 *
 * An option whose measure no product has ranks every product as missing, so the
 * comparator returns the input order and the control appears inert (R13.2).
 *
 * `undefined` when the page carries no product source at all — that is "not
 * known", and gates nothing. An empty array is the opposite claim (sources
 * exist, none of them carries a rankable measure), so the two must not collapse
 * into each other: returning `[]` for "no catalogue found" would silently take
 * every measure option off a page that sorts perfectly well.
 */
const collectCatalogueMeasures = (
  components: Array<CmsComponentProps>,
  ordersProducts: (component: CmsComponentProps) => boolean,
): AttrValue[] | undefined => {
  const known = new Set<string>(Object.values(ATTR_NAMES));
  const present = new Set<AttrValue>();
  let sawProducts = false;

  const readProduct = (product: { attributes?: unknown } | undefined) => {
    const attributes = product?.attributes;
    if (!Array.isArray(attributes)) return;
    sawProducts = true;
    attributes.forEach((attribute) => {
      const name = (attribute as { name?: { name?: string } })?.name?.name;
      if (name && known.has(name)) present.add(name as AttrValue);
    });
  };

  components.forEach((component) => {
    // ProductMetaConfig is the page catalogue the card list renders from...
    (component as ComponentProductMetadata).product_card?.forEach((card) =>
      readProduct(card.productBlock?.product),
    );
    // ...and a block the panel orders carries its own, which the catalogue
    // never sees. Gated: `products` is not a unique field — Swiper Cards holds
    // `{ label, product }` wrappers, and a Selected Products product listing is
    // editorial copy the panel neither sorts nor filters.
    if (!ordersProducts(component)) return;
    const products = (component as { products?: unknown }).products;
    if (Array.isArray(products)) {
      products.forEach((product) =>
        readProduct(product as { attributes?: unknown }),
      );
    }
  });

  return sawProducts ? [...present] : undefined;
};

/**
 * The products the page lists, from the same two sources the measure walk
 * covers: a derived filter group's options are read off these (CMS-1903 R2.1 /
 * R4.2), and a group whose source yields nothing is dropped rather than shown
 * empty (R1.4).
 *
 * Deliberately a flat list with duplicates removed by product uid: the same
 * product can appear in the catalogue and in a Selected Products block, and
 * counting it twice would inflate every option count.
 */
const collectCatalogueProducts = (
  components: Array<CmsComponentProps>,
  ordersProducts: (component: CmsComponentProps) => boolean,
): FilterableProduct[] => {
  const byUid = new Map<string, FilterableProduct>();
  const unkeyed: FilterableProduct[] = [];

  const add = (product: FilterableProduct | undefined) => {
    if (!product) return;
    const uid = product.productUid;
    if (uid) {
      if (!byUid.has(uid)) byUid.set(uid, product);
      return;
    }
    // No uid to dedupe on: kept, because dropping it would understate a count.
    unkeyed.push(product);
  };

  components.forEach((component) => {
    (component as ComponentProductMetadata).product_card?.forEach((card) =>
      add(card.productBlock?.product as FilterableProduct | undefined),
    );
    // Same gate as the measure walk, so the count names only listed products.
    if (!ordersProducts(component)) return;
    const products = (component as { products?: unknown }).products;
    if (Array.isArray(products)) {
      products.forEach((product) => add(product as FilterableProduct));
    }
  });

  return [...byUid.values(), ...unkeyed];
};

/** The broker's id for a plan; a quote's premiums are keyed by it. */
const insuremoUidOf = (product: FilterableProduct): string | undefined => {
  const value = product.attributes?.find(
    (attribute) => attribute?.name?.name === 'INSUREMO_PRODUCT_UID',
  )?.value;
  return typeof value === 'string' ? value : undefined;
};

/**
 * The plans the page can actually show. A travel page renders only what the
 * quote priced, so counting the whole catalogue names more results than the
 * column holds — "34 plans found" above 8 cards (CMS-1542, Kok Fu 23 Sep).
 *
 * Narrowed only once the quote has settled, and only when the catalogue
 * carries the ids to match on: no id anywhere means the payload cannot answer
 * the question, which must never read as "nothing is priced".
 */
const quotedProducts = (
  products: FilterableProduct[],
  travelQuotes: TravelInsuranceQuotesData | undefined,
): FilterableProduct[] => {
  if (!travelQuotes || arePremiumsPending(travelQuotes)) return products;
  if (!products.some(insuremoUidOf)) return products;

  const priced = new Set(
    (travelQuotes.premiums ?? [])
      .map((premium) => premium?.brokerProductCode)
      .filter((code): code is string => !!code),
  );

  return products.filter((product) => {
    const uid = insuremoUidOf(product);
    return !!uid && priced.has(uid);
  });
};

/** Written on the rail itself: the rail's own height, for its `top`. */
export const SORT_FILTER_RAIL_HEIGHT_VAR = '--sort-filter-rail-height';

/**
 * Below lg the slot is `contents`, so the row's `gap-y` spaces the panel; at lg
 * it is a sticky rail, and `items-start` on the row leaves it room to stick.
 * `top` is the smaller of "under the top bar" and "bottom edge on the bottom
 * bar", so a panel taller than the gap scrolls with the page and then sticks by
 * its bottom edge (CMS-2055). Unmeasured, the height reads 0 and the rail pins
 * as a short one. `z-sticky` keeps full-bleed result backgrounds off it.
 */
const SortFilterRail: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof ResizeObserver === 'undefined') return;

    // Below lg the rail is `contents` and measures 0, which nothing reads.
    const publish = () =>
      node.style.setProperty(
        SORT_FILTER_RAIL_HEIGHT_VAR,
        `${node.offsetHeight}px`,
      );

    // Reports once on observe, then whenever a group or "show more" changes
    // the panel's height.
    const resizeObserver = new ResizeObserver(publish);
    resizeObserver.observe(node);

    return () => {
      resizeObserver.disconnect();
      node.style.removeProperty(SORT_FILTER_RAIL_HEIGHT_VAR);
    };
  }, []);

  return (
    <div
      ref={ref}
      data-component="sort-filter-rail"
      data-sort-filter-rail=""
      // eslint-disable-next-line tailwindcss/no-arbitrary-value
      className="contents lg:sticky lg:top-[min(var(--pinned-top-height,0px),calc(100vh-var(--pinned-bottom-height,0px)-var(--sort-filter-rail-height,0px)))] lg:z-sticky lg:block lg:w-70 lg:shrink-0"
    >
      {children}
    </div>
  );
};

/** Everything `BlockMapper` takes, minus the two props this component owns. */
export type SortAndFilterLayoutProps = Omit<
  BlockMapperProps,
  'renderRange' | 'render'
> & {
  /**
   * The page's own panel, from `Page.plSortFilter` / `Page.tiSortFilter`. Absent
   * means the page has no panel and this renders as a pass-through.
   */
  panel?: CmsSortFilterPanel;
};

/**
 * Renders a page's blocks, and the two-column row when the page carries a panel.
 *
 * The panel is a field on Page and Article rather than a block in the components
 * zone (CMS-1893 / CMS-1903), so it has no position of its own: the row opens at
 * the first block the panel orders and closes after the last one.
 *
 * Every container that renders CMS blocks goes through here rather than calling
 * `BlockMapper` directly, so pages and articles cannot diverge. With no panel it
 * is a pass-through.
 */
export const SortAndFilterLayout: React.FC<SortAndFilterLayoutProps> = (
  props,
) => {
  const {
    components,
    vertical,
    isHomePage,
    ContentContainer,
    skipKeys,
    hiddenKeys,
    panel,
  } = props;
  const { brand } = useClientContext();
  const noun = resultNoun(vertical);
  // The mapper's own skip identity, (typename, id): a bare id would also skip
  // a block of another type that happens to share it (CMS-1852).
  const skipKeySet = new Set(skipKeys ?? []);

  // The mapper's own filtering, not a copy of it: these indices are the bounds
  // handed back to it as `renderRange`.
  const visibleComponents = getVisibleBlocks(components);

  // The row opens at the first block the panel orders. Skip-aware, because a
  // skipped block renders nothing and must not open a column of its own.
  const panelIndex = visibleComponents.findIndex(
    (c) =>
      isSortedProductBlock(c, vertical) && !skipKeySet.has(getBlockSkipKey(c)),
  );
  // Hidden blocks (`hiddenKeys`) take no GA row in BlockMapper, so skip them here.
  const hiddenKeySet = new Set(hiddenKeys ?? []);
  const panelGaIndex =
    panelIndex -
    visibleComponents
      .slice(0, Math.max(panelIndex, 0))
      .filter((c) => hiddenKeySet.has(getBlockSkipKey(c))).length;
  const sortFilterPanel = panel;

  // Through the last ordered block, not the contiguous run after the panel: copy
  // interleaved between the table and the card list must not drop the card list
  // out of the row. A block inside that span is in the column either way.
  let rowEndIndex = panelIndex;
  for (let i = panelIndex + 1; i < visibleComponents.length; i += 1) {
    const component = visibleComponents[i];
    // A skipped block renders nothing, so it neither extends the row nor ends it.
    if (skipKeySet.has(getBlockSkipKey(component))) continue;
    if (isSortedProductBlock(component, vertical)) rowEndIndex = i;
  }

  // The blocks that list products beside the panel: the row's own test, plus
  // visibility, since the walks below read `components` rather than the visible list.
  const ordersProducts = useCallback(
    (component: CmsComponentProps) =>
      component?.visible !== false &&
      isSortedProductBlock(component, vertical) &&
      !(skipKeys ?? []).includes(getBlockSkipKey(component)),
    [vertical, skipKeys],
  );

  // Above the early return: a hook cannot be called conditionally.
  const availableMeasures = useMemo(
    () => collectCatalogueMeasures(components, ordersProducts),
    [components, ordersProducts],
  );

  // Derived options are read off the page's own products, so this has to happen
  // where the blocks are visible — and above the early return, since a hook
  // cannot be called conditionally.
  // One walk, two consumers: the derived options and the availability greying
  // must agree about which products the page holds.
  const catalogueProducts = useMemo(
    () => collectCatalogueProducts(components, ordersProducts),
    [components, ordersProducts],
  );

  // The groups keep deriving their options from the whole catalogue, so a
  // quote that prices few plans greys values rather than dropping the group.
  // Only the count and the greying describe what the column actually holds.
  //
  // The page scope's quote — the layout renders beside the blocks, under none
  // of their scopes — read from the calculation store, which holds the travel
  // quote since CMS-1890. Outside a store it reads empty rather than throwing,
  // which matters because this layout also renders in trees that mount no
  // `CmsClientProvider`. No quote simply means no narrowing.
  const { travelQuote: travelQuotesData } = useScopeCalculations();
  const renderableProducts = useMemo(
    () =>
      vertical === 'TRAVEL_INSURANCE'
        ? quotedProducts(catalogueProducts, travelQuotesData)
        : catalogueProducts,
    [catalogueProducts, vertical, travelQuotesData],
  );

  /**
   * Undefined off the eligibility verticals, which reads as "could not
   * enumerate" — no citizenship options, no income control, and every loan
   * resting. One gate rather than one per consumer.
   */
  const eligibilityProducts =
    vertical && ELIGIBILITY_VERTICALS.includes(vertical)
      ? catalogueProducts
      : undefined;

  const sortHidden = sortFilterPanel?.viewConfig?.sortVisible === false;

  /**
   * `filterVisible: false` resolves to no groups rather than hiding the control,
   * so the panel, the chips, Clear All, the empty state and the blocks' own
   * filtering all agree. Hiding only the UI would leave the blocks filtering by
   * a selection the reader can no longer see or clear.
   */
  const filterCategories = useMemo(
    () =>
      sortFilterPanel?.viewConfig?.filterVisible === false
        ? []
        : resolveFilterCategories(
            sortFilterPanel?.config?.categories ?? [],
            catalogueProducts,
          ),
    [sortFilterPanel, catalogueProducts],
  );

  /**
   * `sortVisible` and `filterVisible` are independent switches, so the panel
   * can be either half on its own. With the sort off it is handed an empty
   * option list rather than a special layout: the panel already renders its
   * Sort By section only when it has options, and an empty list also leaves
   * the ordering untouched — nothing would explain a reordered page with no
   * control on it.
   *
   * Both halves off is no panel and no row, which is also the no-panel case.
   */
  if (!sortFilterPanel || (sortHidden && filterCategories.length === 0)) {
    return <BlockMapper {...props} />;
  }

  const authoredOptions = sortHidden ? [] : sortFilterPanel.config?.sortOptions;

  const sortConfig = {
    // The editor's own panel labels, published on the same config the panel
    // already reads so there is no second copy to disagree (CMS-1893 R1.4).
    componentTitle: sortFilterPanel.config?.componentTitle,
    sortButtonLabel: sortFilterPanel.config?.sortButtonLabel,
    // The page config's own options win outright when it carries any; otherwise
    // the view config's default applies to the market list (CMS-1942).
    defaultSort: sortHidden
      ? undefined
      : (sortFilterPanel.viewConfig?.defaultSortOption ??
        authoredOptions?.[0]?.value),
    // The editor's list, in their order — the panel resolves it against what it
    // can rank by rather than rendering a list of its own.
    options: authoredOptions,
    // Picks the fallback list when this page authored no options, so a TI page
    // cannot be offered the PL measures.
    brand,
    availableMeasures,
  };

  // One container around the row, so the columns share the page gutters and the
  // content max-width instead of each getting their own.
  const RowContainer = ContentContainer ?? PlainContainer;

  const stackClassName = classNames({
    'flex flex-col': isHomePage,
    'flex flex-col gap-spacing/gap/y': !isHomePage,
  });

  // Travel only: `TravelPolicySearch` portals its progress, error and empty
  // state in here, so the reader is told what happened beside the panel rather
  // than in a second results panel of its own. Both returns need one, or the
  // portal has no target and the quote reports nothing. `empty:hidden` keeps
  // the slot from taking a row gap on a page with no quote block to fill it.
  const quoteStatusSlot = (className?: string) =>
    vertical === 'TRAVEL_INSURANCE' && (
      <div
        id={TI_QUOTE_STATUS_SLOT_ID}
        className={classNames(className, 'empty:hidden')}
      />
    );

  // The provider wraps the page whenever there is a panel, even when no block
  // on it is orderable: the control still reads and writes `cms_sort`, and the
  // row is a layout question rather than a condition on sorting.
  if (panelIndex < 0) {
    return (
      <ProductSortProvider sort={sortConfig}>
        <ProductFilterProvider
          categories={filterCategories}
          products={renderableProducts}
        >
          <ProductEligibilityProvider products={eligibilityProducts}>
            <div className={stackClassName}>
              <RowContainer>
                <div
                  data-block-row="sort-filter-panel"
                  className="flex flex-col gap-spacing/gap/y lg:flex-row lg:items-start lg:gap-spacing/gap/x"
                >
                  <SortFilterRail>
                    <SortAndFilterPanelWrapper
                      blockIndex={0}
                      vertical={vertical}
                    />
                  </SortFilterRail>
                  {quoteStatusSlot('min-w-0 lg:flex-1')}
                </div>
              </RowContainer>
              {/* Ranged, so the mapper does not add a second stack inside this
                one: it wraps only when it owns the container. */}
              <BlockMapper
                {...props}
                renderRange={[0, visibleComponents.length - 1]}
              />
            </div>
          </ProductEligibilityProvider>
        </ProductFilterProvider>
      </ProductSortProvider>
    );
  }

  return (
    <ProductSortProvider sort={sortConfig}>
      <ProductFilterProvider
        categories={filterCategories}
        products={renderableProducts}
      >
        <ProductEligibilityProvider products={eligibilityProducts}>
          <div className={stackClassName}>
            <BlockMapper {...props} renderRange={[0, panelIndex - 1]} />
            {/* The sticky portal roots span the full page width, so they render
            above the columns rather than inside the one holding their block. */}
            <BlockMapper
              {...props}
              renderRange={[panelIndex, rowEndIndex]}
              render="portals"
            />
            <RowContainer>
              <div
                // A test hook: `docs/testing.md` forbids selecting the row by its
                // Tailwind classes.
                data-block-row="sort-filter-panel"
                className="flex flex-col gap-spacing/gap/y lg:flex-row lg:items-start lg:gap-spacing/gap/x"
              >
                <SortFilterRail>
                  {/* The panel is a page field, so it is rendered here rather than
                  reached through the mapper. It reports the first ordered
                  block's `component_index`, which is what the analytics plan
                  wants. */}
                  <SortAndFilterPanelWrapper
                    blockIndex={panelGaIndex}
                    vertical={vertical}
                  />
                </SortFilterRail>
                <div
                  // Behind skeletons while a shared filtered link is applied (R9.6).
                  {...{ [SORT_FILTER_RESULTS_ATTR]: '' }}
                  className={classNames(stackClassName, 'min-w-0 lg:flex-1')}
                >
                  {/* The count and the sort lead the column, above whatever the
                  quote has to report: an error panel in the slot below would
                  otherwise push them under it, and the reader would be told
                  what happened before being told what they are looking at
                  (CMS-1542, Kok Fu 24 Sep). */}
                  {noun && <ResultsSummary resultNoun={noun} />}
                  {/* Above the results rather than above the row: the chips
                  describe what the results are narrowed to, and on desktop the
                  panel beside them already shows the ticked boxes. */}
                  <ActiveFilterChipsWrapper
                    blockIndex={panelGaIndex}
                    vertical={vertical}
                  />
                  {quoteStatusSlot()}
                  <BlockMapper
                    {...props}
                    renderRange={[panelIndex, rowEndIndex]}
                    render="blocks"
                    ContentContainer={PlainContainer}
                  />
                </div>
              </div>
            </RowContainer>
            <BlockMapper
              {...props}
              renderRange={[rowEndIndex + 1, visibleComponents.length - 1]}
            />
          </div>
        </ProductEligibilityProvider>
      </ProductFilterProvider>
    </ProductSortProvider>
  );
};
